import { Popover } from "@headlessui/react";
import Button, { ButtonColorTypes } from "../../shared-components/Button";
import InputField from "../../shared-components/InputField";
import CheckBox from "../../shared-components/CheckBox";
import { ChangeEvent, useState } from "react";
import { SearchIcon } from "../../shared-components/icons";
import { uniqBy } from "lodash";
import {
  formattedLastActivity,
  formattedLastActivityDateBetween,
} from "../../utils";

export type AssignmentFilterData = {
  key: string;
  label: string;
};

export type AssignmentActionDef = (
  action: AssignmentPopoverActions,
  selection?: AssignmentFilterData[],
  headerKey?: string
) => void;

export enum AssignmentPopoverActions {
  "asc",
  "desc",
  "cancel",
  "ok",
}

type Props = {
  children: React.ReactNode;
  onAction: AssignmentActionDef;
  data: AssignmentFilterData[];
  headerKey: string;
};

function buildShownData(
  data: AssignmentFilterData[],
  headerKey: string,
  filterText: string
) {
  let shownData: AssignmentFilterData[] = [];

  if (headerKey === "email") {
    shownData =
      filterText.length === 0
        ? data
        : data.filter((d) => d.label.includes(filterText));
    return shownData;
  }

  if (headerKey === "lastActive") {
    shownData =
      filterText.length === 0
        ? data
        : data.filter((d) => {
            return d.label.includes(filterText);
          });
    let inBetween = "";
    shownData = shownData.map((d) => {
      if (formattedLastActivityDateBetween(d.label) === "") {
        return { key: d.key, label: "" };
      } else if (formattedLastActivityDateBetween(d.label) === ">45") {
        if (!inBetween) {
          inBetween = d.label;
        }
        return { key: d.key, label: inBetween };
      }
      return {
        key: d.key,
        label: d.label,
      };
    });
    shownData = uniqBy(shownData, "label");
    return shownData;
  }

  if (headerKey === "lastTask") {
    shownData =
      filterText.length === 0
        ? data
        : data.filter((d) => d.label.includes(filterText));
    shownData = uniqBy(shownData, "label");
    return shownData;
  }
  shownData = uniqBy(data, "label");
  return shownData;
}

export function AssignmentPopover({
  children,
  onAction,
  data,
  headerKey,
}: Props) {
  const [selection, setSelection] = useState<AssignmentFilterData[]>([]);
  const [currentSort, setCurrentSort] = useState<"asc" | "desc">("asc");
  const [filterText, setFilterText] = useState("");

  function updateSelection(
    evt: React.ChangeEvent<HTMLInputElement>,
    item: AssignmentFilterData
  ) {
    if (evt.target.checked) {
      setSelection([...selection, item]);
    } else {
      setSelection(selection.filter((s) => s.key !== item.key));
    }
  }

  function changeSort(sort: AssignmentPopoverActions) {
    onAction(sort);

    setCurrentSort(sort === AssignmentPopoverActions.asc ? "asc" : "desc");
  }

  function selectAll() {
    setSelection(data);
  }

  function clear() {
    setSelection([]);
    setFilterText("");
  }

  function filterData(event: ChangeEvent<HTMLInputElement>): void {
    setFilterText(event.target.value);
  }

  function labelFormat(label: string): string {
    if (headerKey === "lastActive") {
      return formattedLastActivity(label);
    }
    return label;
  }
  let shownData: AssignmentFilterData[] = buildShownData(
    data,
    headerKey,
    filterText
  );

  return (
    <Popover className="w-full relative">
      <Popover.Button className={"w-full"}>{children}</Popover.Button>

      <Popover.Panel className="absolute z-100 bg-white w-96 shadow-md rounded-xl p-4">
        {({ close }) => (
          <>
            <div
              className="mb-3 cursor-pointer"
              onClick={() => {
                changeSort(AssignmentPopoverActions.asc);
                close();
              }}
            >
              Sort A to Z
              {currentSort === "asc" && (
                <span className="inline-block text-green-500 ml-2">
                  &#10003;
                </span>
              )}
            </div>
            <div
              className="mb-3 cursor-pointer"
              onClick={() => {
                changeSort(AssignmentPopoverActions.desc);
                close();
              }}
            >
              Sort Z to A
              {currentSort === "desc" && (
                <span className="inline-block text-green-500 ml-2">
                  &#10003;
                </span>
              )}
            </div>

            <div className="flex justify-between gap-3 mb-3">
              <Button
                text={"Select all"}
                colorType={ButtonColorTypes.cancel}
                className="flex-1"
                onClick={selectAll}
              ></Button>
              <Button
                text={"Clear"}
                colorType={ButtonColorTypes.cancel}
                className="flex-1"
                onClick={clear}
              ></Button>
            </div>
            <div className="mb-3 relative">
              <InputField
                name="search-user-name"
                classes="w-full"
                onChange={filterData}
                value={filterText}
              ></InputField>
              <SearchIcon className="absolute right-2 top-2 text-gray-400" />
            </div>

            <div className="border h-24 rounded-md overflow-auto mb-3 p-2">
              {shownData.map((item) => (
                <div className="flex gap-2 items-center" key={item.key}>
                  <CheckBox
                    onChange={(evt) => updateSelection(evt, item)}
                    checked={selection.some((sel) => sel.key === item.key)}
                  />
                  <span>
                    {item.label.length === 0
                      ? "(blank)"
                      : labelFormat(item.label)}
                  </span>
                </div>
              ))}
            </div>
            {selection.length > 0 && <div>{selection.length} selected</div>}
            <div className="flex justify-end gap-4">
              <Button
                text={"Cancel"}
                colorType={ButtonColorTypes.cancel}
                onClick={() => {
                  onAction(AssignmentPopoverActions.cancel);
                  close();
                }}
              ></Button>
              <Button
                text={"OK"}
                onClick={() => {
                  onAction(AssignmentPopoverActions.ok, selection, headerKey);
                  close();
                }}
              ></Button>
            </div>
          </>
        )}
      </Popover.Panel>
    </Popover>
  );
}
